import { db } from "config/firebase";
import { createHash } from "utils/helperFunctions";
import { doc, getDoc, updateDoc, runTransaction } from "firebase/firestore";

const addPlayerToGame = async (
  gameId: string,
  playerData: any,
  expTime: number,
): Promise<void> => {
  const gameRef = doc(db, "games", gameId);
  const updatedPlayerData = { ...playerData, timeStamp: expTime };

  try {
    await runTransaction(db, async (transaction) => {
      const docSnap = await transaction.get(gameRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        const { currentPlayers, playerlist } = data;
        
        // Create new objects instead of mutating existing ones
        const updatedCurrentPlayers = { ...currentPlayers, [updatedPlayerData.id]: true };
        const updatedPlayerlist = { ...playerlist, [updatedPlayerData.id]: updatedPlayerData };

        transaction.update(gameRef, { 
          playerlist: updatedPlayerlist, 
          currentPlayers: updatedCurrentPlayers 
        });
      } else {
        console.error(`Game ${gameId} not found`);
      }
    });
  } catch (err) {
    console.error("Error in addPlayerToGame transaction:", err);
    throw err;
  }
};

const removePlayerFromGame = async (
  gameId: string,
  playerId: string,
): Promise<any> => {
  const gameRef = doc(db, "games", gameId);

  try {
    await runTransaction(db, async (transaction) => {
      const docSnap = await transaction.get(gameRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        const { currentPlayers, playerlist } = data;

        // Create new objects without the removed player
        const updatedCurrentPlayers = { ...currentPlayers };
        const updatedPlayerlist = { ...playerlist };
        
        delete updatedCurrentPlayers[playerId];
        delete updatedPlayerlist[playerId];

        transaction.update(gameRef, {
          currentPlayers: updatedCurrentPlayers,
          playerlist: updatedPlayerlist,
        });
      }
    });
    return "success";
  } catch (err) {
    console.error("Error removing player from game:", err);
    return err;
  }
};

const addPlayerToTableManually = async (
  gameId: string,
  playerName: string,
): Promise<{ msg: string }> => {
  if (playerName === "") {
    return { msg: "Please enter a name" };
  }
  const gameRef = doc(db, "games", gameId);
  const id = `Host${createHash(playerName)}`;
  const playerData = {
    id,
    displayName: `${playerName} (Guest)`,
    manuallyAdded: true,
    timeStamp: Date.now(),
  };

  try {
    const docSnap = await getDoc(gameRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      const { currentPlayers, playerlist } = data;

      if (currentPlayers[id]) {
        return { msg: `${playerData.displayName} is already on the table` };
      }

      const updatedCurrentPlayers = { ...currentPlayers, [id]: true };
      const updatedPlayerlist = { ...playerlist, [id]: playerData };
      await updateDoc(gameRef, {
        currentPlayers: updatedCurrentPlayers,
        playerlist: updatedPlayerlist,
      });
      return { msg: `${playerData.displayName} added to table successfully` };
    }
    return { msg: "Game not found" };
  } catch {
    return { msg: "Error adding player" };
  }
};

const confirmAddPlayer = async (
  gameId: string,
  playerId: string,
): Promise<any> => {
  const gameRef = doc(db, "games", gameId);

  try {
    const docSnap = await getDoc(gameRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      const { playerlist } = data;
      
      // Create new object instead of mutating existing one
      const updatedPlayerlist = { 
        ...playerlist, 
        [playerId]: { ...playerlist[playerId], timeStamp: Date.now() }
      };

      await updateDoc(gameRef, { playerlist: updatedPlayerlist });
    }
  } catch (err) {
    console.log("error confirming player", err);
    return err;
  }
};

const declineJoinGame = async (
  gameId: string,
  playerId: string,
): Promise<any> => {
  const gameRef = doc(db, "games", gameId);

  try {
    const docSnap = await getDoc(gameRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      const { currentPlayers, playerlist } = data;
      
      // Create new objects instead of mutating existing ones
      const updatedCurrentPlayers = { ...currentPlayers };
      const updatedPlayerlist = { ...playerlist };
      delete updatedCurrentPlayers[playerId];
      delete updatedPlayerlist[playerId];

      await updateDoc(gameRef, {
        currentPlayers: updatedCurrentPlayers,
        playerlist: updatedPlayerlist,
      });
    }
  } catch (err) {
    return err;
  }
};

export default {
  addPlayerToGame,
  removePlayerFromGame,
  addPlayerToTableManually,
  confirmAddPlayer,
  declineJoinGame,
};
